import React, { useEffect, useState } from "react";
import { AreaChart, Area, ResponsiveContainer, Tooltip } from "recharts";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * TrustSparkline — tiny 12-month area chart of Trust Score history.
 *
 * Rendered inside the passport hero next to the marquee number so
 * buyers instantly see the direction of travel, not just the snapshot.
 * If there is only one data point, we widen it to two so Recharts still
 * draws an anchor dot. If there are zero points we render nothing (the
 * hero looks fine without a sparkline).
 */
export const TrustSparkline = ({ vin, currentScore, tone = "#39FF14" }) => {
  const [data, setData] = useState([]);
  useEffect(() => {
    if (!vin || !API_BASE) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/battery/trust_history/${encodeURIComponent(vin)}`)
      .then((r) => (r.ok ? r.json() : { points: [] }))
      .then((d) => {
        if (cancelled) return;
        let pts = (d.points || []).map((p) => ({
          ts: p.ts,
          score: p.trust_score,
          label: new Date(p.ts).toLocaleDateString(undefined, {
            month: "short", day: "numeric",
          }),
        }));
        // Always end on the current score so the chart matches the hero.
        if (typeof currentScore === "number") {
          if (pts.length === 0 || pts[pts.length - 1].score !== currentScore) {
            pts = [...pts, { ts: new Date().toISOString(), score: currentScore, label: "now" }];
          }
        }
        // Single point? Duplicate so we get a visible line.
        if (pts.length === 1) pts = [{ ...pts[0], label: "start" }, { ...pts[0], label: "now" }];
        setData(pts);
      })
      .catch(() => setData([]));
    return () => { cancelled = true; };
  }, [vin, currentScore]);

  if (!vin || data.length < 2) return null;

  const first = data[0].score;
  const last = data[data.length - 1].score;
  const trend = last - first;
  const trendColor = trend >= 0 ? "#39FF14" : "#FF6B57";
  const gradId = `trust-spark-grad-${vin.slice(-4)}`;
  // If the only "history" is the synthesised anchor + current score, we
  // have effectively no trend — show a friendlier "first snapshot" line.
  const isFirstSnapshot = data.length === 2 && data[0].score === data[1].score;

  return (
    <div className="min-w-[128px] pl-3" data-testid="passport-trust-sparkline">
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500 mb-0.5">
        12-mo trend
      </div>
      <div className="h-10 w-32 min-h-[40px]">
        <ResponsiveContainer width="100%" height="100%" minHeight={40} minWidth={80}>
          <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 2 }}>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={tone} stopOpacity={0.55} />
                <stop offset="100%" stopColor={tone} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Tooltip
              cursor={false}
              wrapperStyle={{ outline: "none" }}
              contentStyle={{
                background: "rgba(0,0,0,0.9)",
                border: "1px solid rgba(255,255,255,0.14)",
                borderRadius: "6px",
                padding: "4px 8px",
                fontSize: "10px",
                fontFamily: "monospace",
              }}
              formatter={(v) => [`${v}/100`, "Trust"]}
              labelFormatter={(l) => l}
            />
            <Area
              type="monotone"
              dataKey="score"
              stroke={tone}
              strokeWidth={2}
              fill={`url(#${gradId})`}
              isAnimationActive={false}
              dot={false}
              activeDot={{ r: 3, fill: tone, stroke: "none" }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="font-mono text-[10px] mt-0.5" style={{ color: trendColor }}>
        {isFirstSnapshot ? (
          <span className="text-zinc-500">First snapshot · trend builds with each passport</span>
        ) : (
          <>{trend > 0 ? "+" : ""}{trend} pts vs. {new Date(data[0].ts).toLocaleDateString(undefined, { month: "short", year: "2-digit" })}</>
        )}
      </div>
    </div>
  );
};
